export const curriculumHero = {
  title: 'カリキュラム概要',
  titleEn: 'CURRICULUM',
  lead: '当アカデミーのカリキュラムは、理論と実践を往復しながら段階的に技能を積み上げます。座学・実技・ロールプレイング・評価の循環で、確かな力を育みます。',
};

export const learningCycle = {
  title: '学びの全体像',
  titleEn: 'Learning Cycle',
  description: '当アカデミーの学びは、聞いて終わりにはしません。理論で理解し、実技で試し、ロールプレイングで磨く——この往復を繰り返して技能を身につけます。各コースで難易度を段階的に上げながら、着実にプロフェッショナルへと導きます。',
  steps: [
    {
      step: '01',
      title: '講義（理論）',
      titleEn: 'Lecture',
      description: '協会監修の教科書を用いて、執事の歴史・作法・ホスピタリティ理論を体系的に学びます。知識の土台を固める段階です。',
      icon: 'ri-book-open-line',
    },
    {
      step: '02',
      title: '実技（演習）',
      titleEn: 'Practice',
      description: '講義で学んだ内容を、少人数クラスで実際に手を動かして試します。テーブルセッティングからワインサービスまで、講師の指導のもと反復練習します。',
      icon: 'ri-hand-sanitizer-line',
    },
    {
      step: '03',
      title: 'ロールプレイング',
      titleEn: 'Role-Play',
      description: '実際の邸宅やホテルを想定したスタジオで、リアルな場面を再現。受講生同士または講師とで役割を演じ、臨機応変な対応力を養います。',
      icon: 'ri-group-line',
    },
    {
      step: '04',
      title: '評価・フィードバック',
      titleEn: 'Evaluation',
      description: '講師が各人の所作・対応を細かく観察し、個別にフィードバック。改善点を明確にして次の学習につなげる、循環型の向上サイクルです。',
      icon: 'ri-chat-check-line',
    },
  ],
};

export const fourPillars = {
  title: 'カリキュラムの4つの柱',
  titleEn: 'Four Pillars',
  description: 'カリキュラムは四つの柱で構成されています。実践トレーニング、体系的な教材、習得スキルの明確化、そして進捗の評価です。それぞれが有機的につながり、総合力を育みます。',
  pillars: [
    {
      title: '実践トレーニング',
      titleEn: 'Practical Training',
      description: '実技とロールプレイングを中心とした少人数制の訓練。スタジオでの本格的な環境で、即戦力となる実践力を磨きます。',
      href: '/training',
      icon: 'ri-user-star-line',
      imageSeq: 'pillars-training',
    },
    {
      title: '使用教材',
      titleEn: 'Textbooks & Materials',
      description: '一般社団法人全日本執事協会が監修する全15冊の専門教科書(基礎・応用・専門の各5冊)。講義と自習の両方で活用し、修了後も参考資料として役立ちます。',
      href: '/materials',
      icon: 'ri-book-2-line',
      imageSeq: 'pillars-materials',
    },
    {
      title: '習得スキル一覧',
      titleEn: 'Skill Map',
      description: '基礎知識・実践スキル・コミュニケーション・問題解決・国際感覚の5領域で、段階的に習得する全スキルを一覧化しています。',
      href: '/skills',
      icon: 'ri-bar-chart-2-line',
      imageSeq: 'pillars-skills',
    },
    {
      title: '評価・進捗管理',
      titleEn: 'Assessment & Progress',
      description: '各実習ごとの講師評価と自己評価を組み合わせ、強みと課題を可視化。段階的な成長を支える進捗管理を行います。',
      href: '#evaluation-note',
      icon: 'ri-checkbox-circle-line',
      imageSeq: 'pillars-evaluation',
      isAnchor: true,
    },
  ],
};

export const courseGoals = {
  title: 'コース別の到達目標',
  titleEn: 'Course Goals by Level',
  description: '以下は各コース修了時点での到達目標の目安です。コースが進むにつれて、より高度な知識と実践力を段階的に身につけます。',
  note: '※これは到達目標であり、修了を保証する実績値ではありません。',
  perspectives: [
    { key: '基础知识', label: '基礎知識', labelEn: 'Fundamental Knowledge', description: '執事の歴史、作法、ホスピタリティ理論の理解度' },
    { key: '実践スキル', label: '実践スキル', labelEn: 'Practical Skills', description: 'テーブルサービス、衣類管理、ワイン知識などの実践力' },
    { key: 'コミュニケーション', label: 'コミュニケーション', labelEn: 'Communication', description: '来客応対、状況判断、言葉遣い・所作を含む総合的な対人能力' },
    { key: '問題解決', label: '問題解決', labelEn: 'Problem Solving', description: '予期せぬ事態への対応、危機管理、柔軟な判断力' },
    { key: '国際感覚', label: '国際感覚', labelEn: 'Global Mindset', description: '異文化理解、英語対応、国際的なマナーとプロトコル' },
  ],
  levels: [
    {
      course: 'エッセンシャル',
      courseEn: 'ESSENTIAL',
      icon: 'ri-seedling-line',
      goals: [80, 70, 60, 50, 40],
    },
    {
      course: 'アドバンス',
      courseEn: 'ADVANCED',
      icon: 'ri-stack-line',
      goals: [90, 85, 80, 75, 65],
    },
    {
      course: 'マスター',
      courseEn: 'MASTER',
      icon: 'ri-vip-crown-line',
      goals: [95, 95, 90, 90, 85],
    },
  ],
};

export const textbooks = {
  title: '教材の体系',
  titleEn: 'Textbook System',
  description: '一般社団法人全日本執事協会が監修する全15冊の専門教科書群(基礎のE群・応用のA群・専門のM群、各5冊)。コースによって使用する冊数が異なり、段階的に知識を深めていきます。詳細は使用教材ページをご覧ください。',
  groups: [
    {
      key: 'essential',
      title: 'E群 基礎教科書（全コース共通・5冊）',
      note: '全コース共通で提供',
      books: [
        {
          title: '『E1 執事学入門：歴史・文化・職業倫理』',
          description: '英国発祥の執事文化の歴史から現代に至るまでの変遷、執事としての職業倫理と心構えを学ぶ入門書。全コース共通の基本テキストです。',
          courses: '全コース共通',
        },
        {
          title: '『E2 ハウスキーピングの理論と実践』',
          description: '邸宅の清掃・洗濯・衣類管理から家政運営の基礎理論まで、ハウスキーピングの実務を体系的に学ぶ一冊です。',
          courses: '全コース共通',
        },
        {
          title: '『E3 サービスの基本とおもてなしの心』',
          description: 'テーブルマナー、来客応対、おもてなしの精神を軸に、サービスの基本所作を身につける実践テキストです。',
          courses: '全コース共通',
        },
        {
          title: '『E4 コミュニケーションとビジネスマナー』',
          description: '敬語・言葉遣いから電話応対、ビジネス文書まで、執事に求められる対人スキルとマナーを網羅します。',
          courses: '全コース共通',
        },
        {
          title: '『E5 英国紳士の教養とたしなみ』',
          description: '英国紳士文化に根差した教養、身だしなみ、会話術など、品格あるたしなみを学ぶ一冊です。',
          courses: '全コース共通',
        },
      ],
    },
    {
      key: 'advanced',
      title: 'A群 応用教科書（アドバンス・マスター・5冊）',
      note: 'アドバンスはE群5冊も提供（計10冊）',
      books: [
        {
          title: '『A1 家政管理と財務運営』',
          description: '家計簿・予算管理から外部業者との折衝まで、邸宅運営における財務・家政管理の実務を学びます。',
          courses: 'アドバンス・マスター',
        },
        {
          title: '『A2 ビジネスサポートと秘書業務』',
          description: 'スケジュール管理、文書作成、来客対応など、雇主のビジネスを支える秘書的実務を体系化しています。',
          courses: 'アドバンス・マスター',
        },
        {
          title: '『A3 食事サービスとテーブルマナー』',
          description: 'フォーマルディナーの提供手順、ワイン・ティーサービス、テーブルコーディネートを学ぶ実践書です。',
          courses: 'アドバンス・マスター',
        },
        {
          title: '『A4 イベント企画と空間演出』',
          description: 'パーティーやレセプションの企画運営、空間演出、装花・照明までを扱う応用テキストです。',
          courses: 'アドバンス・マスター',
        },
        {
          title: '『A5 ライフスタイル管理と特別サポート』',
          description: '雇主の日常生活全般のマネジメントと、個別ニーズに応じた特別なサポート技術を学びます。',
          courses: 'アドバンス・マスター',
        },
      ],
    },
    {
      key: 'master',
      title: 'M群 専門教科書（マスター限定・5冊）',
      note: 'マスターは全15冊を提供',
      books: [
        {
          title: '『M1 国際プロトコルと各国文化』',
          description: '各国の外交儀礼・文化的タブー・国際的なマナーを収録し、グローバルに活躍する知識を提供します。',
          courses: 'マスター',
        },
        {
          title: '『M2 社交技術とエスコート』',
          description: '社交の場でのふるまい、エスコート技術、会話術など、上級執事に求められる社交スキルを学びます。',
          courses: 'マスター',
        },
        {
          title: '『M3 トラベルサポートと移動管理』',
          description: '旅程の手配から車両・移動手段の管理まで、雇主の移動を支えるトラベルサポートの実務を学びます。',
          courses: 'マスター',
        },
        {
          title: '『M4 危機管理とトラブル対応』',
          description: '医療緊急時、自然災害、セキュリティ事案など、邸宅で起こりうる危機への対応力を養います。',
          courses: 'マスター',
        },
        {
          title: '『M5 VVIPサービスとマネジメント』',
          description: '最上級の顧客層に向けたサービス設計とスタッフマネジメント、執事としての総合力を極める最終巻です。',
          courses: 'マスター',
        },
      ],
    },
  ],
};

export const curriculumFaqs = [
  {
    question: '座学だけですか？',
    answer: 'いいえ。講義で学んだ内容を必ず実技とロールプレイングで実践し、講師のフィードバックで仕上げる循環型のカリキュラムです。',
  },
  {
    question: '教材はありますか？',
    answer: '協会監修の教科書を使用します。コースにより使用する冊数が異なり、卒業後の参考資料としても活用できます。',
  },
  {
    question: 'どんなスキルが身につきますか？',
    answer: '基礎の所作からテーブルサービス、ワイン、危機対応、管理能力まで段階的に習得します。詳しくは習得スキル一覧をご覧ください。',
  },
];

export const curriculumRelatedPages = [
  {
    title: '実践トレーニング',
    description: '実技とロールプレイング中心の訓練',
    href: '/training',
  },
  {
    title: '使用教材',
    description: '協会監修の教科書全15冊',
    href: '/materials',
  },
  {
    title: '習得スキル一覧',
    description: '身につく5つのスキル領域',
    href: '/skills',
  },
  {
    title: 'コース一覧・比較',
    description: '3コースの違いを一覧で確認',
    href: '/courses',
  },
  {
    title: '教育理念・方針',
    description: '私たちが大切にする教育の考え方',
    href: '/philosophy',
  },
];